/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@compiled/react';
import { NAVIGATION_ITEM_ID } from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/error-boundary/index.tsx';
import { testIdGenerate } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
import { useSetCustomThemeData } from '@jira/navigation-apps__common/src/controllers/custom-theme-context/index.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { CustomLogo, AppLogo } from '@atlaskit/navigation-system/top-nav-items';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useCustomLogo, useCustomIcon } from '../../controllers/custom-logo/index.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import type { JiraProjectType } from './__generated__/ProductHomeWithQuery.graphql';
import { JSMAndCSMLogo } from './jsm-and-csm-logo/index.tsx';
import { getProductIcon, getLogoLabel } from './utils.tsx';

const customLogoAltText = defineMessage({
	defaultMessage: 'Jira homepage',
	id: 'atlassian-navigation.product-home.custom-logo-alt-text',
	description:
		'Alternative text for the custom product logo image in the top navigation bar that links to the Jira homepage',
});

const customIconAltText = defineMessage({
	defaultMessage: 'Custom Jira icon',
	id: 'atlassian-navigation.product-home.custom-icon-alt-text',
	description:
		'Alternative text for the custom product icon image displayed in the top navigation bar',
});

const HOME_URL = '/jira';

type Props = {
	projectType?: JiraProjectType | null | undefined;
};

const CreateCustomLogoComponent = (props: { alt: string; src: string; testId: string }) => () => (
	<CustomImgNav4 {...props} />
);

export const ProductHome = (props: Props): JSX.Element => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.HOME}>
		<ProductHomeContent {...props} />
	</TopLevelErrorBoundary>
);

function ProductHomeContent({ projectType }: Props) {
	const testIdPrefix = testIdGenerate('product-home-');
	const logoUrl = useCustomLogo();
	const iconUrl = useCustomIcon();
	const productName = useProductName();
	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.LOGO,
	});

	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	useEffect(() => {
		setCustomThemeData({
			hasCustomLogo: !!logoUrl,
		});
	}, [logoUrl, setCustomThemeData]);

	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const navigateToHome = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
		(ev) => {
			triggerAnalytics();
			if (ev.ctrlKey || ev.metaKey) return;
			ev.preventDefault();
			push(HOME_URL);
		},
		[triggerAnalytics, push],
	);
	const { icon: ProductIcon, name: logoName } = useMemo(
		() => getProductIcon(productName, testIdPrefix),
		[productName, testIdPrefix],
	);

	const Logo = useMemo(
		() =>
			CreateCustomLogoComponent({
				alt: formatMessage(customLogoAltText),
				src: logoUrl || '',
				testId: `${testIdPrefix}-logo`,
			}),
		[logoUrl, formatMessage, testIdPrefix],
	);

	const Icon = useMemo(
		() =>
			logoUrl && iconUrl
				? () => (
						<CustomImgNav4
							alt={formatMessage(customIconAltText)}
							src={iconUrl}
							testId={`${testIdPrefix}-icon`}
						/>
					)
				: ProductIcon,
		[logoUrl, iconUrl, ProductIcon, testIdPrefix, formatMessage],
	);

	const label = getLogoLabel(formatMessage, productName);

	const {
		hasSoftwareAccess,
		hasServiceDeskAccess,
		hasCustomerServiceAccess,
		hasProductDiscoveryAccess,
	} = useApplicationPermissions();

	// check if the user has access to JSM and CSM only
	const isJSMAndCSMOnlyAccessible =
		hasServiceDeskAccess &&
		hasCustomerServiceAccess &&
		!hasSoftwareAccess &&
		!hasProductDiscoveryAccess;

	if (!logoUrl && isJSMAndCSMOnlyAccessible) {
		return (
			<Box testId={`${testIdPrefix}-container`}>
				<JSMAndCSMLogo
					navigateToHome={navigateToHome}
					testIdPrefix={testIdPrefix}
					homeURL={HOME_URL}
					projectType={projectType}
				/>
			</Box>
		);
	}

	return (
		<Box testId={`${testIdPrefix}-container`}>
			{!logoUrl ? (
				<AppLogo
					name={logoName}
					label={label}
					icon={Icon}
					href={HOME_URL}
					onClick={navigateToHome}
				/>
			) : (
				// Render custom logo component if logoUrl is present (even if the icon is not custom)
				<CustomLogo
					label={label}
					icon={Icon}
					logo={Logo}
					href={HOME_URL}
					onClick={navigateToHome}
				/>
			)}
		</Box>
	);
}

type CustomImgProps = {
	alt: string;
	src: string;
	testId: string;
};

function CustomImgNav4({ alt, src, testId }: CustomImgProps) {
	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	const handleLoad = useCallback(
		(e: React.SyntheticEvent<HTMLImageElement>) => {
			// Image loaded
			setCustomThemeData({
				customLogoSize: {
					width: e.currentTarget.naturalWidth,
					height: e.currentTarget.naturalHeight,
				},
			});
		},
		[setCustomThemeData],
	);

	return <img src={src} alt={alt} data-testid={testId} onLoad={handleLoad} />;
}
